import { useEffect, useMemo, useRef } from 'react';
import { useDisclosure } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { ArrowLeft, Check, ChevronRight, ExternalLink, Play, RotateCcw, SkipForward } from 'lucide-react';

import { GET_ACADEMY_CATEGORIES, GET_ME } from '../graphql/queries';
import { trackEvent, trackPageView } from '../utils/gt';
import {
  channelCount,
  clock,
  isWatched,
  lastTrail,
  minutesLabel,
  nextLessonIndex,
  resumeAt,
  secondsLeft,
  trailLength,
  trailsFrom,
  useAcademyProgress,
  watchedCount,
  watchedShare,
} from '../utils/academy';
import { AcademyPlayer } from '../components/academy/AcademyPlayer';

import academyVidEn from '../assets/pro-only-vids/academy-en.mp4';
import academyVidBr from '../assets/pro-only-vids/academy-br.mp4';

import { ModalUpgradePro } from '../components/ModalUpgradePro';
import { PreviewArt } from '../components/app/PreviewArt';

// The lessons a phone lists under the one playing
const UP_NEXT = 5;

// A trail's share watched: a green arc from 12 o'clock, a check once it's whole
const TrailRing = ({ share }) => {
  const size = 46;
  const stroke = 4.5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  return (
    <span className="mt-ac-ring" aria-hidden="true">
      <svg className="mt-ac-ring__svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle className="mt-ac-ring__track" cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={stroke} />
        {share > 0 && (
          <circle
            className="mt-ac-ring__arc"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - Math.max(share, 0.03))}
          />
        )}
      </svg>
      {share >= 1 ? <Check className="mt-ac-ring__check" /> : <b>{Math.round(share * 100)}%</b>}
    </span>
  );
};

// A video's picture, with its length and how much of it has been seen
const Thumb = ({ video, share, large = false }) => (
  <span className="mt-ac-thumb">
    <img src={`https://i.ytimg.com/vi/${video.youtubeId}/${large ? 'hqdefault' : 'mqdefault'}.jpg`} alt="" loading="lazy" />
    {video.durationSec ? <span className="mt-ac-thumb__length">{clock(video.durationSec)}</span> : null}
    {share > 0 && (
      <span className="mt-ac-thumb__seen">
        <i style={{ width: `${Math.max(share, 0.04) * 100}%` }} />
      </span>
    )}
  </span>
);

export const Academy = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });
  const ui = (key, options) => t(`pages.Academy.ui.${key}`, options);

  const { data: dataCategories, loading: isLoadingCategories } = useQuery(GET_ACADEMY_CATEGORIES);
  const { data: dataMe } = useQuery(GET_ME);

  const me = dataMe?.me;
  const isPro = me?.plan === 'pro' && (me?.subscriptionStatus === 'active' || me?.subscriptionStatus === 'trialing');
  // Locked only once the account is known to be free, so Pro members never see the blur flash in
  const isLocked = !!me && !isPro;

  const trails = useMemo(() => trailsFrom(dataCategories?.academyCategories?.data, language), [dataCategories, language]);
  const { progress, save } = useAcademyProgress({ skip: !isPro });
  const { isOpen: isUpgradeOpen, onOpen: onUpgradeOpen, onOpenChange: onUpgradeOpenChange, onClose: onUpgradeClose } = useDisclosure();

  // The view comes from the address: ?t=<trail> opens a trail, &l=<n> its lesson n
  const openTrail = trails.find((trail) => trail.slug === searchParams.get('t')) || null;
  const lessonParam = Number(searchParams.get('l'));
  const lessonIndex =
    openTrail && searchParams.has('l') && Number.isInteger(lessonParam) && openTrail.videos[lessonParam] ? lessonParam : null;
  const view = lessonIndex !== null ? 'lesson' : openTrail && !isDesktop ? 'trail' : 'home';
  // A lesson opened by a tap plays at once; one reached by a link or the browser's back and forward waits for play
  const playOnOpen = useRef(false);

  useEffect(() => {
    trackPageView('academy', `${t('appUrl')}/academy`, '/academy');
  }, [t]);

  // The player picked up "play at once" when it mounted; whatever opens next waits for its own tap
  useEffect(() => {
    playOnOpen.current = false;
  }, [location.key]);

  useEffect(() => {
    if (isLocked) onUpgradeOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocked]);

  const viewKey = lessonIndex !== null ? `lesson:${openTrail.slug}` : openTrail ? `trail:${openTrail.slug}` : 'home';

  /** Opens a trail (and a lesson in it). Each entry remembers the view it came from, for the back control. */
  const go = (slug, lesson = null, { replace = false } = {}) => {
    playOnOpen.current = lesson !== null;
    const params = slug ? { t: slug } : {};
    if (lesson !== null) params.l = String(lesson);
    setSearchParams(params, { replace, state: { from: replace ? location.state?.from : viewKey } });
    window.scrollTo({ top: 0 });
  };

  // Up one level: the browser's back when that's where the member came from, otherwise a replace
  const goUp = () => {
    const parent = lessonIndex !== null ? (isDesktop ? 'home' : `trail:${openTrail.slug}`) : openTrail && !isDesktop ? 'home' : null;
    if (!parent) {
      navigate(window.history.state?.idx > 0 ? -1 : '/dashboard');
      return;
    }
    if (location.state?.from === parent || (isDesktop && location.state?.from?.startsWith('trail:'))) {
      navigate(-1);
      return;
    }
    if (parent === 'home') {
      // Desktop keeps the trail open beside the list
      go(isDesktop ? openTrail.slug : null, null, { replace: true });
    } else {
      go(openTrail.slug, null, { replace: true });
    }
  };

  const backLabel = lessonIndex !== null ? openTrail.name : view === 'trail' ? ui('trails') : ui('back');

  const watchLesson = (trail, index) => {
    trackEvent(`academy_video_${trail.slug}`, `Academy - Watch: ${trail.videos[index].title}`, 'academy', true);
    go(trail.slug, index, { replace: lessonIndex !== null });
  };

  // ── Pieces ──────────────────────────────────────────────────────────

  const trailRow = (trail, isCurrent) => {
    const seen = watchedCount(trail, progress);
    return (
      <li key={trail.slug}>
        <button
          type="button"
          className="mt-ac-trail"
          aria-current={isCurrent || undefined}
          onClick={() => {
            trackEvent(`academy_trail_${trail.slug}`, `Academy - Trail: ${trail.slug}`, 'academy', true);
            go(trail.slug);
          }}
        >
          <TrailRing share={seen / trail.videos.length} />
          <span className="mt-ac-trail__text">
            <b>{trail.name}</b>
            <small>
              {seen ? ui('seenOf', { seen, total: trail.videos.length }) : ui('lessons', { count: trail.videos.length })} ·{' '}
              {minutesLabel(trailLength(trail))}
            </small>
          </span>
          <ChevronRight className="mt-ac-trail__chevron" aria-hidden="true" />
        </button>
      </li>
    );
  };

  const lessonRow = (trail, video, { isPlaying = false } = {}) => {
    const done = isWatched(progress, video);
    const isNext = !done && video.index === nextLessonIndex(trail, progress);
    const state = isPlaying ? 'is-playing' : done ? 'is-done' : isNext ? 'is-next' : '';
    return (
      <li key={video.youtubeId}>
        <button
          type="button"
          className={`mt-ac-lesson ${state}`}
          aria-current={isPlaying || undefined}
          aria-label={ui(done ? 'lessonLabelWatched' : 'lessonLabel', { n: video.index + 1, title: video.title })}
          onClick={() => watchLesson(trail, video.index)}
        >
          <span className="mt-ac-lesson__mark" aria-hidden="true">
            {isPlaying ? <Play /> : done ? <Check /> : video.index + 1}
          </span>
          <Thumb video={video} share={watchedShare(progress, video)} />
          <span className="mt-ac-lesson__text">
            <b>{video.title}</b>
            <small>
              {video.channel}
              {done ? ` · ${ui('watchedTag')}` : ''}
            </small>
          </span>
        </button>
      </li>
    );
  };

  const timeLeft = (video) => {
    const left = secondsLeft(progress, video);
    return left === null ? minutesLabel(video.durationSec) : ui('timeLeft', { time: minutesLabel(left) });
  };

  const nextUp = (trail) => {
    const index = nextLessonIndex(trail, progress);
    const video = trail.videos[index];
    const seen = watchedCount(trail, progress);
    const isAllSeen = seen === trail.videos.length;
    const label = isAllSeen
      ? ui('watchAgain')
      : secondsLeft(progress, video) !== null
        ? ui('continueLesson', { n: index + 1 })
        : seen
          ? ui('watchLesson', { n: index + 1 })
          : ui('startTrail');
    return (
      <div className="mt-ac-next">
        <button type="button" className="mt-ac-next__thumb" onClick={() => watchLesson(trail, index)} aria-label={video.title}>
          <Thumb video={video} share={watchedShare(progress, video)} large />
        </button>
        <div>
          <h3 className="mt-ac-next__title">{video.title}</h3>
          <p className="mt-ac-next__meta">
            {video.channel} · {timeLeft(video)}
          </p>
          <button type="button" className="mt-btn mt-btn--primary" onClick={() => watchLesson(trail, index)}>
            {isAllSeen ? <RotateCcw aria-hidden="true" /> : <Play aria-hidden="true" />}
            {label}
          </button>
        </div>
      </div>
    );
  };

  const trailHead = (trail, HeadingTag = 'h2') => {
    const seen = watchedCount(trail, progress);
    return (
      <>
        <HeadingTag className="mt-ac-trail-head__name">{trail.name}</HeadingTag>
        <p className="mt-ac-trail-head__meta">
          {ui('lessons', { count: trail.videos.length })} · {ui('ofVideo', { time: minutesLabel(trailLength(trail)) })} ·{' '}
          {ui('channels', { count: channelCount(trail) })}
        </p>
        <div className="mt-ac-trail-head__progress">
          <span
            className="mt-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={trail.videos.length}
            aria-valuenow={seen}
            aria-label={ui('seenLabel')}
          >
            <span style={{ width: `${(seen / trail.videos.length) * 100}%` }} />
          </span>
          <span>{ui('seenOf', { seen, total: trail.videos.length })}</span>
        </div>
        {nextUp(trail)}
        <ol className="mt-ac-lessons" aria-label={ui('lessonsLabel')}>
          {trail.videos.map((video) => lessonRow(trail, video))}
        </ol>
      </>
    );
  };

  // ── Views ───────────────────────────────────────────────────────────

  const renderHome = () => {
    if (isDesktop) {
      const selected = openTrail || lastTrail(trails, progress);
      return (
        <div className="mt-ac-home">
          <aside className="mt-ac-home__side">
            <h1 className="mt-ac-title">{ui('title')}</h1>
            <p className="mt-ac-sub">{ui('sub')}</p>
            <ul className="mt-ac-trails" aria-label={ui('trails')}>
              {trails.map((trail) => trailRow(trail, trail.slug === selected.slug))}
            </ul>
          </aside>
          <section className="mt-ac-home__main" aria-label={selected.name}>
            {trailHead(selected)}
          </section>
        </div>
      );
    }
    // Phones: pick up where the member left off, then the trails
    const trail = lastTrail(trails, progress);
    const index = nextLessonIndex(trail, progress);
    const video = trail.videos[index];
    const hasStarted = Object.keys(progress).length > 0;
    return (
      <>
        <h1 className="mt-ac-title">{ui('title')}</h1>
        <p className="mt-ac-sub">{ui('sub')}</p>
        <article className="mt-ac-continue">
          <button type="button" className="mt-ac-continue__thumb" onClick={() => watchLesson(trail, index)} aria-label={video.title}>
            <Thumb video={video} share={watchedShare(progress, video)} large />
          </button>
          <div className="mt-ac-continue__body">
            <h2>{video.title}</h2>
            <p>
              {trail.name} · {ui('lessonOf', { n: index + 1, total: trail.videos.length })} · {timeLeft(video)}
            </p>
            <button type="button" className="mt-btn mt-btn--primary" onClick={() => watchLesson(trail, index)}>
              <Play aria-hidden="true" />
              {ui(hasStarted ? 'continue' : 'start')}
            </button>
          </div>
        </article>
        <h2 className="mt-ac-section">{ui('trails')}</h2>
        <ul className="mt-ac-trails">{trails.map((item) => trailRow(item, false))}</ul>
      </>
    );
  };

  const renderLesson = () => {
    const trail = openTrail;
    const video = trail.videos[lessonIndex];
    const done = isWatched(progress, video);
    const seen = watchedCount(trail, progress);
    const isLast = lessonIndex === trail.videos.length - 1;
    const listed = isDesktop ? trail.videos : trail.videos.slice(lessonIndex + 1, lessonIndex + 1 + UP_NEXT);
    return (
      <div className="mt-ac-watch">
        <div className="mt-ac-stage">
          <AcademyPlayer
            video={video}
            autoplay={playOnOpen.current}
            startAt={resumeAt(progress, video)}
            onProgress={(position, duration) => save(video.youtubeId, { position, duration })}
            onEnded={() => save(video.youtubeId, { done: true })}
          />
          <div className="mt-ac-stage__body">
            <h1 className="mt-ac-stage__title">{video.title}</h1>
            <p className="mt-ac-stage__meta">
              <b>{video.channel}</b>
              {video.durationSec ? ` · ${clock(video.durationSec)}` : ''} · {ui('lessonOf', { n: lessonIndex + 1, total: trail.videos.length })}
            </p>
            <div className="mt-ac-stage__row">
              <div className="mt-ac-stage__actions">
                <button
                  type="button"
                  className="mt-btn mt-btn--quiet"
                  aria-pressed={done}
                  onClick={() => {
                    trackEvent('academy_mark_watched', 'Academy - Mark Watched', 'academy', true);
                    save(video.youtubeId, { done: !done });
                  }}
                >
                  <Check aria-hidden="true" />
                  {ui(done ? 'watched' : 'markWatched')}
                </button>
                {isLast ? (
                  <button
                    type="button"
                    className="mt-btn mt-btn--primary"
                    onClick={() => {
                      trackEvent('academy_finish_trail', `Academy - Finish Trail: ${trail.slug}`, 'academy', true);
                      save(video.youtubeId, { done: true });
                      go(trail.slug, null, { replace: true });
                    }}
                  >
                    <Check aria-hidden="true" />
                    {ui('finishTrail')}
                  </button>
                ) : (
                  <button type="button" className="mt-btn mt-btn--primary" onClick={() => watchLesson(trail, lessonIndex + 1)}>
                    <SkipForward aria-hidden="true" />
                    {ui('nextLesson')}
                  </button>
                )}
              </div>
              <a className="mt-ac-stage__youtube" href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer">
                {ui('openYoutube')}
                <ExternalLink aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
        <aside className="mt-ac-playlist" aria-label={ui('playlistLabel')}>
          <div className="mt-ac-playlist__top">
            <div className="mt-ac-playlist__head">
              <h2>{isDesktop ? trail.name : ui('upNext')}</h2>
              <span>{ui('seenOf', { seen, total: trail.videos.length })}</span>
            </div>
            <span className="mt-progress" aria-hidden="true">
              <span style={{ width: `${(seen / trail.videos.length) * 100}%` }} />
            </span>
          </div>
          <ol className="mt-ac-lessons">{listed.map((item) => lessonRow(trail, item, { isPlaying: item.index === lessonIndex }))}</ol>
          {!isDesktop && (
            <button type="button" className="mt-text-btn mt-ac-playlist__all" onClick={() => go(trail.slug)}>
              {ui('seeAll', { count: trail.videos.length })}
              <ChevronRight aria-hidden="true" />
            </button>
          )}
        </aside>
      </div>
    );
  };

  let content;
  if (isLoadingCategories) {
    content = <div className="mt-ac-loading" aria-busy="true" />;
  } else if (trails.length === 0) {
    content = <p className="mt-lib-empty">{t('pages.Academy.emptyState')}</p>;
  } else if (view === 'lesson' && !isLocked) {
    content = renderLesson();
  } else if (view === 'trail' && !isLocked) {
    content = trailHead(openTrail, 'h1');
  } else {
    content = renderHome();
  }

  return (
    <div className="mt-root mt-root--app mt-academy-page">
      {/* Desktop has the Central in the rail: the bar is only the way from a lesson back to its trail */}
      {(!isDesktop || lessonIndex !== null) && (
        <header className="mt-editor-bar">
          <button type="button" className="mt-editor-bar__back" onClick={goUp} aria-label={backLabel}>
            <ArrowLeft aria-hidden="true" />
            <span className="mt-editor-bar__back-label">{backLabel}</span>
          </button>
          <span />
          <span />
        </header>
      )}

      {isLocked && (
        <section className="mt-academy-locked">
          <PreviewArt kind="video" badge="lock" />
          <div className="mt-academy-locked__text">
            <h2>{ui('lockedTitle')}</h2>
            <p>{ui('lockedText')}</p>
          </div>
          <button
            type="button"
            className="mt-btn mt-btn--primary"
            onClick={() => {
              trackEvent('academy_see_pro', 'Academy - See Pro', 'academy', true);
              navigate('/plans');
            }}
          >
            {ui('lockedCta')}
          </button>
        </section>
      )}

      {/* Free accounts see the trails behind the Pro offer, blurred and out of reach */}
      <div className={`mt-academy${isLocked ? ' mt-locked' : ''}`} aria-hidden={isLocked || undefined} inert={isLocked ? '' : undefined}>
        {content}
      </div>

      <ModalUpgradePro
        isOpen={isUpgradeOpen}
        onOpenChange={onUpgradeOpenChange}
        onClose={onUpgradeClose}
        title={t('pages.Academy.proTitle')}
        description={t('pages.Academy.proDesc')}
        onDismiss={() => navigate('/dashboard')}
        videoSrc={language === 'pt' ? academyVidBr : academyVidEn}
      />
    </div>
  );
};
